import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Inbox, ChevronDown, ChevronUp, FileDown, Trash2, Sparkles, FileText } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import { AdminNav } from "../components/AdminNav";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const STATUSES = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "quotation_sent", label: "Quotation Sent" },
  { value: "sample_requested", label: "Sample Requested" },
  { value: "negotiation", label: "Negotiation" },
  { value: "order_confirmed", label: "Order Confirmed" },
  { value: "closed", label: "Closed" },
];

const STATUS_STYLES = {
  new: "bg-clay/15 text-clay border-clay/40",
  contacted: "bg-white/5 text-offwhite/60 border-white/20",
  quotation_sent: "bg-forest/20 text-emerald-300 border-forest",
  sample_requested: "bg-forest/20 text-emerald-300 border-forest",
  negotiation: "bg-amber-500/15 text-amber-300 border-amber-600/40",
  order_confirmed: "bg-emerald-500/15 text-emerald-300 border-emerald-600/40",
  closed: "bg-white/5 text-offwhite/40 border-white/15",
};

const inputClass =
  "h-10 rounded-none border-white/15 bg-charcoal text-offwhite text-sm focus-visible:ring-clay focus-visible:ring-offset-0";

function InquiryDetails({ inquiry }) {
  const fields = [
    ["Email", inquiry.email],
    ["Phone / WhatsApp", inquiry.phone || "—"],
    ["Country", inquiry.country],
    ["Quantity", inquiry.quantity],
    ["Packaging", inquiry.packaging || "—"],
    ["Incoterm", inquiry.incoterm || "—"],
    ["Destination Country", inquiry.destination_country || "—"],
    ["Destination Port", inquiry.destination_port || "—"],
    ["Payment Preference", inquiry.payment_term || "—"],
    ["Target Delivery", inquiry.delivery_date || "—"],
  ];
  return (
    <>
      {fields.map(([label, value]) => (
        <p key={label} className="flex flex-col gap-0.5">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-offwhite/35">{label}</span>
          <span className="text-offwhite/80 break-all">{value}</span>
        </p>
      ))}
      {(inquiry.specification || inquiry.message) && (
        <div className="sm:col-span-2 space-y-3">
          {inquiry.specification && (
            <p className="flex flex-col gap-0.5">
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-offwhite/35">Specification</span>
              <span className="text-offwhite/80 whitespace-pre-wrap">{inquiry.specification}</span>
            </p>
          )}
          {inquiry.message && (
            <p className="flex flex-col gap-0.5">
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-offwhite/35">Message</span>
              <span className="text-offwhite/80 whitespace-pre-wrap">{inquiry.message}</span>
            </p>
          )}
        </div>
      )}
    </>
  );
}

function DraftPanel({ inquiry, draft, draftType, onChange }) {
  const label =
    draftType === "quote"
      ? "AI Quotation Outline — fill in pricing before sending"
      : "AI Draft Reply — review before sending";
  const subject =
    draftType === "quote"
      ? `Indicative Quotation — ${inquiry.product} | HAQILA`
      : `RE: Your inquiry — ${inquiry.product} | HAQILA`;

  return (
    <div className="sm:col-span-2 space-y-2" data-testid={`inquiry-draft-${inquiry.id}`}>
      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-clay">{label}</span>
      <Textarea
        value={draft}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-none border-clay/40 bg-charcoal text-offwhite text-sm min-h-[180px] focus-visible:ring-clay focus-visible:ring-offset-0"
      />
      <div className="flex gap-3">
        <button
          data-testid={`inquiry-draft-copy-${inquiry.id}`}
          onClick={() => {
            navigator.clipboard.writeText(draft);
            toast.success("Draft copied to clipboard");
          }}
          className="text-xs font-semibold border border-white/20 hover:border-offwhite text-offwhite/70 px-4 py-2 transition-colors duration-300"
        >
          Copy Draft
        </button>
        <a
          data-testid={`inquiry-draft-email-${inquiry.id}`}
          href={`mailto:${inquiry.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(draft)}`}
          className="text-xs font-semibold bg-forest hover:bg-moss text-offwhite px-4 py-2 transition-colors duration-300"
        >
          Open in Email
        </a>
      </div>
    </div>
  );
}

function InquiryCard({ inquiry, onStatusChange, onSaveNotes, onDelete }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(inquiry.internal_notes || "");
  const [draft, setDraft] = useState("");
  const [draftType, setDraftType] = useState("reply");
  const [drafting, setDrafting] = useState(null);

  const generateDraft = async (type) => {
    setDrafting(type);
    try {
      const endpoint = type === "quote" ? "draft-quote" : "draft-reply";
      const res = await api.post(`/api/inquiries/${inquiry.id}/${endpoint}`);
      setDraft(res.data.draft);
      setDraftType(type);
      toast.success(type === "quote" ? "Quotation outline generated" : "Draft reply generated");
    } catch {
      toast.error("Failed to generate draft");
    } finally {
      setDrafting(null);
    }
  };

  return (
    <div data-testid={`inquiry-card-${inquiry.id}`} className="border border-white/10 bg-white/[0.03]">
      <button
        data-testid={`inquiry-toggle-${inquiry.id}`}
        onClick={() => setOpen(!open)}
        className="w-full flex flex-wrap items-center gap-x-5 gap-y-2 px-5 sm:px-7 py-5 text-left"
      >
        <span
          className={`text-[10px] font-bold uppercase tracking-[0.2em] border px-2.5 py-1 ${
            STATUS_STYLES[inquiry.status] || STATUS_STYLES.new
          }`}
        >
          {STATUSES.find((s) => s.value === inquiry.status)?.label || inquiry.status}
        </span>
        {inquiry.request_type === "sample" && (
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] border border-white/25 text-offwhite/60 px-2.5 py-1">
            Sample
          </span>
        )}
        <span className="font-display font-bold text-offwhite text-sm">{inquiry.full_name}</span>
        <span className="text-sm text-offwhite/50">{inquiry.company_name}</span>
        <span className="text-xs text-clay font-semibold">{inquiry.product}</span>
        <span className="text-xs text-offwhite/40">{inquiry.country}</span>
        <span className="ml-auto flex items-center gap-3 text-xs text-offwhite/40">
          {new Date(inquiry.created_at).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>
      {open && (
        <div className="px-5 sm:px-7 pb-6 pt-2 border-t border-white/10 grid sm:grid-cols-2 gap-x-10 gap-y-3 text-sm">
          <InquiryDetails inquiry={inquiry} />
          <div className="sm:col-span-2 pt-2 space-y-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1.5">
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-offwhite/35">Status</span>
                <Select value={inquiry.status} onValueChange={(v) => onStatusChange(inquiry.id, v)}>
                  <SelectTrigger data-testid={`inquiry-status-${inquiry.id}`} className={`${inputClass} w-52`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-offwhite/35">
                Internal Notes (private)
              </span>
              <Textarea
                data-testid={`inquiry-notes-${inquiry.id}`}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add internal notes — never shown publicly"
                className="rounded-none border-white/15 bg-charcoal text-offwhite text-sm min-h-[70px] focus-visible:ring-clay focus-visible:ring-offset-0"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                data-testid={`inquiry-draft-reply-${inquiry.id}`}
                onClick={() => generateDraft("reply")}
                disabled={!!drafting}
                className="inline-flex items-center gap-2 text-xs font-semibold bg-clay hover:bg-[#7a4d2d] disabled:opacity-60 text-offwhite px-4 py-2 transition-colors duration-300"
              >
                {drafting === "reply" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Draft Reply with AI
              </button>
              <button
                data-testid={`inquiry-draft-quote-${inquiry.id}`}
                onClick={() => generateDraft("quote")}
                disabled={!!drafting}
                className="inline-flex items-center gap-2 text-xs font-semibold bg-forest hover:bg-moss disabled:opacity-60 text-offwhite px-4 py-2 transition-colors duration-300"
              >
                {drafting === "quote" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                Draft Quotation
              </button>
              <button
                data-testid={`inquiry-save-notes-${inquiry.id}`}
                onClick={() => onSaveNotes(inquiry.id, notes)}
                className="text-xs font-semibold bg-forest hover:bg-moss text-offwhite px-4 py-2 transition-colors duration-300"
              >
                Save Notes
              </button>
              <button
                data-testid={`inquiry-delete-${inquiry.id}`}
                onClick={() => onDelete(inquiry)}
                className="inline-flex items-center gap-2 text-xs font-semibold border border-red-900/50 text-red-400/70 hover:border-red-400 hover:text-red-400 px-4 py-2 transition-colors duration-300"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete
              </button>
            </div>
          </div>
          {draft && <DraftPanel inquiry={inquiry} draft={draft} draftType={draftType} onChange={setDraft} />}
        </div>
      )}
    </div>
  );
}

function InquiryFilters({ statusFilter, setStatusFilter, productFilter, setProductFilter, countryFilter, setCountryFilter, products }) {
  return (
    <div data-testid="inquiry-filters" className="grid sm:grid-cols-3 gap-3 mb-8">
      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger data-testid="filter-status" className={inputClass}>
          <SelectValue placeholder="All statuses" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Statuses</SelectItem>
          {STATUSES.map((s) => (
            <SelectItem key={s.value} value={s.value}>
              {s.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={productFilter} onValueChange={setProductFilter}>
        <SelectTrigger data-testid="filter-product" className={inputClass}>
          <SelectValue placeholder="All products" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Products</SelectItem>
          {products.map((p) => (
            <SelectItem key={p} value={p}>
              {p}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        data-testid="filter-country"
        value={countryFilter}
        onChange={(e) => setCountryFilter(e.target.value)}
        placeholder="Filter by country…"
        className={inputClass}
      />
    </div>
  );
}

export default function AdminInquiries() {
  const [inquiries, setInquiries] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [productFilter, setProductFilter] = useState("all");
  const [countryFilter, setCountryFilter] = useState("");
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/api/inquiries")
      .then((res) => setInquiries(res.data.inquiries))
      .catch(() => navigate("/admin/login"));
  }, [navigate]);

  const products = useMemo(
    () => (inquiries ? [...new Set(inquiries.map((inq) => inq.product))] : []),
    [inquiries]
  );

  const filtered = useMemo(() => {
    if (!inquiries) return [];
    return inquiries.filter((inq) => {
      if (statusFilter !== "all" && inq.status !== statusFilter) return false;
      if (productFilter !== "all" && inq.product !== productFilter) return false;
      if (countryFilter && !inq.country.toLowerCase().includes(countryFilter.toLowerCase())) return false;
      return true;
    });
  }, [inquiries, statusFilter, productFilter, countryFilter]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/api/inquiries/${id}/status`, { status });
      setInquiries((prev) => prev.map((inq) => (inq.id === id ? { ...inq, status } : inq)));
      toast.success("Status updated");
    } catch {
      toast.error("Failed to update status");
    }
  };

  const saveNotes = async (id, notes) => {
    try {
      await api.put(`/api/inquiries/${id}/notes`, { internal_notes: notes });
      setInquiries((prev) => prev.map((inq) => (inq.id === id ? { ...inq, internal_notes: notes } : inq)));
      toast.success("Notes saved");
    } catch {
      toast.error("Failed to save notes");
    }
  };

  const deleteInquiry = async (inquiry) => {
    if (!window.confirm(`Delete inquiry from ${inquiry.full_name} (${inquiry.company_name})?`)) return;
    try {
      await api.delete(`/api/inquiries/${inquiry.id}`);
      setInquiries((prev) => prev.filter((inq) => inq.id !== inquiry.id));
      toast.success("Inquiry deleted");
    } catch {
      toast.error("Failed to delete inquiry");
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/api/inquiries/export/csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "haqila-inquiries.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  let listContent;
  if (inquiries === null) {
    listContent = (
      <div className="flex items-center gap-3 text-offwhite/50">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading inquiries…
      </div>
    );
  } else if (filtered.length === 0) {
    listContent = (
      <div
        data-testid="inquiries-empty"
        className="border border-white/10 bg-white/[0.03] p-14 flex flex-col items-center gap-4 text-center"
      >
        <Inbox className="w-8 h-8 text-offwhite/30" />
        <p className="text-offwhite/50 text-sm">
          {inquiries.length === 0
            ? "No inquiries yet. New quote requests will appear here."
            : "No inquiries match the current filters."}
        </p>
      </div>
    );
  } else {
    listContent = (
      <div data-testid="inquiries-list" className="space-y-4">
        {filtered.map((inquiry) => (
          <InquiryCard
            key={inquiry.id}
            inquiry={inquiry}
            onStatusChange={updateStatus}
            onSaveNotes={saveNotes}
            onDelete={deleteInquiry}
          />
        ))}
      </div>
    );
  }

  return (
    <>
      <SEO title="Inquiries | HAQILA Admin" description="HAQILA inquiry management." />
      <div data-testid="admin-inquiries-page" className="bg-charcoal min-h-screen pt-32 pb-24">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-10">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-clay mb-2">HAQILA Admin</p>
              <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-offwhite">
                Buyer Inquiries
              </h1>
            </div>
            <AdminNav active="Inquiries">
              <button
                data-testid="export-csv-button"
                onClick={exportCsv}
                disabled={exporting}
                className="inline-flex items-center gap-2 text-xs font-semibold bg-clay hover:bg-[#7a4d2d] disabled:opacity-60 text-offwhite px-4 py-2.5 transition-colors duration-300"
              >
                {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
                Export CSV
              </button>
            </AdminNav>
          </div>

          <InquiryFilters
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            productFilter={productFilter}
            setProductFilter={setProductFilter}
            countryFilter={countryFilter}
            setCountryFilter={setCountryFilter}
            products={products}
          />

          {listContent}
        </div>
      </div>
    </>
  );
}
